import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MedicalScheme } from '@xmedical/core';
import { Observable } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { XmedicalUiModule } from '@xmedical/ui';
import { MedicalAidFormShape } from '../patient-form.service';

@Component({
  selector: 'xcs-patient-medical-aid-fieldset',
  templateUrl: './patient-medical-aid-fieldset.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    XmedicalUiModule,
  ],
})
export class PatientMedicalAidFieldsetComponent {
  readonly group = input.required<FormGroup<MedicalAidFormShape>>();

  // Search callback supplied by the form (originally from the container's
  // MedicalSchemesRepositoryService).
  readonly medicalSchemeSearch = input.required<(query: string) => Observable<MedicalScheme[]>>();

  // Bridge form value changes into a signal. Switching on `group()` lets the
  // subscription follow if the parent ever swaps the input reference.
  readonly hasScheme = toSignal(
    toObservable(this.group).pipe(
      switchMap(g => g.controls.scheme.valueChanges.pipe(
        startWith(g.controls.scheme.value),
        map(v => !!v),
      )),
    ),
    { initialValue: false },
  );

  searchSchemes = (query: string): Observable<MedicalScheme[]> => {
    return this.medicalSchemeSearch()(query);
  };

  setMedicalScheme(scheme: MedicalScheme): void {
    const c = this.group().controls;
    c.schemeId.setValue(scheme.id);
    c.scheme.setValue(scheme.name);
    c.destinationCode.setValue(scheme.destinationCode);
  }

  clearMedicalScheme(): void {
    const c = this.group().controls;
    c.schemeId.setValue('');
    c.scheme.setValue('');
    c.destinationCode.setValue('');
  }
}
